import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, User, Bot, Loader2, Filter, Users, MessageSquare, ExternalLink } from 'lucide-react';
import '../styles/personaInterview.css';

function PersonaInterview() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Message formatting function from Huxley
  const formatMessage = useCallback((md) => {
    // Handle multiple levels of escaping from webhook
    let cleanedMd = md
      .replace(/\\\\\\\\n/g, '\n')  // Quadruple escaped newlines
      .replace(/\\\\n/g, '\n')      // Double escaped newlines
      .replace(/\\n/g, '\n')        // Single escaped newlines
      .replace(/\\\\\\\\"/g, '"')   // Quadruple escaped quotes
      .replace(/\\\\"/g, '"')       // Double escaped quotes
      .replace(/\\"/g, '"')         // Single escaped quotes
      .replace(/^"/, '')            // Remove leading quote
      .replace(/"$/, '');           // Remove trailing quote

    const blocks = cleanedMd.trim().split(/\n{2,}/);
    const html = blocks.map(block => {
      if (/^###\s/.test(block)) return `<h3>${block.slice(4)}</h3>`;
      if (/^##\s/.test(block))  return `<h2>${block.slice(3)}</h2>`;
      if (/^#\s/.test(block))   return `<h1>${block.slice(2)}</h1>`;

      if (/^- /.test(block)) {
        const items = block
          .split('\n')
          .map(line => `<li>${line.replace(/^- /, '')}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }

      const inner = block
        .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="chart-image" onclick="openImageModal(\'$2\')" />')
        .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        .replace(/(\w+)\[(https?:\/\/[^\]]+)\]/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+?)\*/g, '<em style="color: #64748b; font-style: normal;">$1</em>')
        .replace(/\n/g, '<br />');

      return `<p>${inner}</p>`;
    }).join('');

    return html.replace(/^<p><\/p>/, '');
  }, []);

  // Chat state
  const [chat, setChat] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Persona selection state
  const [selectedPersona, setSelectedPersona] = useState(location.state?.selectedPersona || null);
  const [personaFilters, setPersonaFilters] = useState({
    generation_target: '', // Seed artist/genre
    actual_audience: '', // New filter for actual audience identification
    location: '',
    genre: '',
    profile_name: ''
  });

  // Available personas from database
  const [availablePersonas, setAvailablePersonas] = useState([]);
  const [personasLoading, setPersonasLoading] = useState(true);
  const [personasError, setPersonasError] = useState(null);

  const chatWindowRef = useRef(null);
  const textareaRef = useRef(null);

  // Fetch personas from database
  useEffect(() => {
    const fetchPersonas = async () => {
      try {
        setPersonasLoading(true);
        setPersonasError(null);

        // Build query string from filters - map directly to database fields
        const queryParams = new URLSearchParams();
        if (personaFilters.generation_target) queryParams.append('generation_target', personaFilters.generation_target);
        if (personaFilters.actual_audience) queryParams.append('actual_audience', personaFilters.actual_audience);
        if (personaFilters.location) queryParams.append('location', personaFilters.location);
        if (personaFilters.genre) queryParams.append('genre', personaFilters.genre);
        if (personaFilters.profile_name) queryParams.append('profile_name', personaFilters.profile_name);
        queryParams.append('limit', '50'); // Get up to 50 personas

        const response = await fetch(`https://n8n.iniai.cloud/webhook/get-synthetic-fans?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Transform database data to match our UI format but preserve original data
        const transformedPersonas = data.map(persona => ({
          // UI display fields
          id: persona.foundation_id,
          name: persona.profile_name.replace(/_/g, ' '), // Remove underscores
          title: extractTitle(persona.demographic_summary),
          industry: extractIndustry(persona.foundation_id, persona.cultural_context),
          department: extractDepartment(persona.demographic_summary, persona.cultural_context),
          experience: extractExperience(persona.demographic_summary),
          company: extractCompany(persona.cultural_context, persona.demographic_summary),
          description: persona.demographic_summary || 'Music fan profile',
          traits: extractTraits(persona.demographic_summary, persona.cultural_context),
          // Original database fields for webhook
          original: {
            foundation_id: persona.foundation_id,
            profile_name: persona.profile_name,
            demographic_summary: persona.demographic_summary,
            cultural_context: persona.cultural_context,
            profile_data: persona.profile_data,
            generation_target: persona.generation_target,
            generation_prompt: persona.generation_prompt
          }
        }));

        setAvailablePersonas(transformedPersonas);
      } catch (error) {
        console.error('Error fetching personas:', error);
        setPersonasError('Failed to load personas. Please try again.');
      } finally {
        setPersonasLoading(false);
      }
    };

    fetchPersonas();
  }, [personaFilters]); // Refetch when filters change

  // Helper functions to extract data from database fields
  const extractTitle = (demographic) => {
    if (!demographic) return 'Fan';

    // Handle comma-separated format like "Marcus, Industrial Equipment Operator, $52K, recovering addict"
    const parts = demographic.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      const potentialTitle = parts[1];
      // Check if second part looks like a job title (not a salary or other info)
      if (potentialTitle && !potentialTitle.match(/^\$|\d+K|years?\s+clean|male|female/i)) {
        return potentialTitle;
      }
    }

    // Look for various job title patterns
    const patterns = [
      /(?:works? as|job|occupation|is)[:\s]+([\w\s]+?)(?:[,.\n]|$)/i,
      /(doctor|nurse|teacher|engineer|developer|manager|consultant|analyst|designer|chef|bartender|electrician|plumber|lawyer|accountant|student|researcher|artist|writer|musician|operator|bookstore\s+manager|development\s+coordinator|record\s+store\s+owner|casting\s+director\s+assistant|marketing\s+coordinator|dental\s+hygienist|claims\s+adjuster|music\s+journalist|blogger|project\s+manager|communications\s+coordinator|uber\s+driver|shift\s+supervisor|qa\s+tester)/i,
      /(\w+)\s+(?:in|at)\s+(?:the\s+)?[\w\s]+/i
    ];

    for (const pattern of patterns) {
      const match = demographic.match(pattern);
      if (match && match[1]) {
        const title = match[1].trim();
        // Clean up common prefixes
        return title.replace(/^(a|an|the)\s+/i, '');
      }
    }

    return 'Fan';
  };

  const extractIndustry = (foundationId, cultural) => {
    // Extract artist from foundation_id pattern (e.g., "jelly_roll_20250923_1135")
    if (foundationId && foundationId.includes('_')) {
      const artistPart = foundationId.split('_').slice(0, -2).join(' '); // Remove date/time parts
      const artistName = artistPart.replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return `${artistName} Audience`;
    }

    // Fallback to extracting from cultural context for actual industry
    if (cultural) {
      const text = cultural.toLowerCase();
      if (text.includes('tech') || text.includes('software')) return 'Technology';
      if (text.includes('healthcare') || text.includes('medical')) return 'Healthcare';
      if (text.includes('education') || text.includes('school')) return 'Education';
      if (text.includes('finance') || text.includes('bank')) return 'Finance';
    }

    return 'Music Audience';
  };

  const extractDepartment = (demographic, cultural) => {
    if (!demographic && !cultural) return 'Fanbase';

    const text = `${demographic || ''} ${cultural || ''}`.toLowerCase();

    // Extract actual job/role context
    if (text.includes('engineer') || text.includes('developer')) return 'Engineering';
    if (text.includes('doctor') || text.includes('nurse')) return 'Medical';
    if (text.includes('teacher') || text.includes('professor')) return 'Education';
    if (text.includes('manager') || text.includes('director')) return 'Management';
    if (text.includes('student')) return 'Student Life';
    if (text.includes('union') || text.includes('trade')) return 'Labor';

    return 'Community';
  };

  const extractExperience = (demographic) => {
    if (!demographic) return 'Music Fan';

    const text = demographic.toLowerCase();
    if (text.includes('senior') || text.includes('veteran') || text.includes('experienced')) return 'Experienced';
    if (text.includes('junior') || text.includes('entry') || text.includes('new')) return 'Entry Level';
    if (text.includes('student') || text.includes('recent')) return 'Student/Recent';

    return 'Professional';
  };

  const extractCompany = (cultural, demographic) => {
    if (!cultural && !demographic) return 'Independent';

    const text = `${cultural || ''} ${demographic || ''}`;

    // Look for specific company/workplace mentions first
    const workMatch = text.match(/(?:works? at|company|employed by)[:\s]+([^,.\n]+)/i);
    if (workMatch) return workMatch[1].trim();

    // Extract location from cultural_context - handle various formats
    if (cultural) {
      // Clean up the cultural context and extract main location
      let location = cultural
        .replace(/\s+Context$/, '') // Remove "Context" suffix
        .replace(/Music Scene|Tech Industry|Indie Scene|Urban Economic Pressure|Class Dynamics/, '') // Remove descriptors
        .replace(/\s+$/, '') // Trim trailing spaces
        .split('/')[0] // Take first part if slash-separated
        .split(',')[0]; // Take first part if comma-separated

      // Handle specific location formats
      if (location.includes('Appalachian')) return location;
      if (location.includes('Eastern Kentucky')) return 'Eastern Kentucky';
      if (location.includes('Kentucky') && location.includes('Nashville')) return 'Kentucky/Nashville';
      if (location.includes('Brooklyn')) return 'Brooklyn, NY';
      if (location.includes('Capitol Hill')) return 'Seattle, WA';
      if (location.includes('Bay Area') || location.includes('Silicon Valley')) return 'San Francisco Bay Area';
      if (location.includes('Rust Belt')) return 'Pittsburgh, PA';

      // Standard city extraction
      if (location.length > 2) return location;
    }

    return 'Various';
  };

  const extractTraits = (demographic, cultural) => {
    const traits = [];
    const text = `${demographic || ''} ${cultural || ''}`.toLowerCase();

    // Extract gender traits (order matters - check female first!)
    if (text.includes('non-binary') || text.includes('nonbinary') || text.includes('non binary')) {
      traits.push('Non-binary');
    } else if (text.includes('female')) {
      traits.push('Female');
    } else if (text.includes(' male') || text.match(/\bmale\b/)) {
      traits.push('Male');
    }

    // Extract specific life circumstances (higher priority)
    if (text.includes('recovering') || text.includes('clean') || text.includes('sober')) traits.push('In Recovery');
    if (text.includes('single parent') || text.includes('single mom') || text.includes('single dad')) traits.push('Single Parent');
    else if (text.includes('parent')) traits.push('Parent');

    // Extract work/class status (prioritize more specific)
    if (text.includes('union') && text.includes('professional')) {
      traits.push('Union Professional'); // More specific than either alone
    } else if (text.includes('union') || (text.includes('worker') && !text.includes('professional'))) {
      traits.push('Working Class');
    } else if (text.includes('professional')) {
      traits.push('Professional');
    }

    // Extract education
    if (text.includes('college') || text.includes('university')) traits.push('College Educated');
    if (text.includes('student')) traits.push('Student');

    // Extract community connection
    if (text.includes('local') || text.includes('community')) traits.push('Community Oriented');

    return traits.length > 0 ? traits.slice(0, 3) : ['Music Fan']; // Limit to 3 traits
  };

  // Auto-scroll to bottom
  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [chat]);

  // Since we're fetching filtered data from backend, just use all available personas
  const filteredPersonas = availablePersonas;

  // Generate session ID
  const generateSessionId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  };

  // Start conversation with selected persona
  const startConversation = async (persona) => {
    // Generate new session ID for this conversation
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
    setSelectedPersona(persona);
    setChat([{
      id: Date.now(),
      type: 'persona',
      content: formatMessage(`Hello! My name is ${persona.name} and I'm excited to chat with you.`),
      timestamp: new Date()
    }]);
  };

  // Handle message submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim() || loading || !selectedPersona) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query.trim(),
      timestamp: new Date()
    };

    setChat(prevChat => [...prevChat, userMessage]);
    const currentQuery = query;
    setQuery('');
    setLoading(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 60000); // 1 minute timeout

    try {
      // Generate session ID if not exists
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        currentSessionId = generateSessionId();
        setSessionId(currentSessionId);
      }

      // Call the synth_interview webhook following Huxley pattern
      const result = await fetch('https://n8n.iniai.cloud/webhook/synth_interview', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
        },
        signal: controller.signal,
        body: JSON.stringify([{
          query: currentQuery,
          generation_target: selectedPersona.original.generation_target,
          profile_name: selectedPersona.original.profile_name,
          location: selectedPersona.original.cultural_context,
          genre: selectedPersona.original.profile_data,
          sessionId: currentSessionId,
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          }
        }])
      });

      clearTimeout(timeoutId);
      const data = await result.json();

      if (Array.isArray(data) && data.length > 0) {
        const response = data[0].response || data[0].output;
        if (response) {
          const assistantMessage = {
            id: Date.now() + 1,
            type: 'persona',
            content: formatMessage(response),
            timestamp: new Date()
          };
          setChat(prevChat => [...prevChat, assistantMessage]);
        }
      }
    } catch (error) {
      clearTimeout(timeoutId);

      const errorMessage = error.name === 'AbortError' || error.code === 'ECONNABORTED'
        ? 'This query is taking longer than expected. Try simplifying your question.'
        : error.response?.data?.message || 'An error occurred while processing your request.';

      setChat(prevChat => [...prevChat, {
        id: Date.now() + 1,
        type: 'persona',
        content: errorMessage,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleTextareaChange = (e) => {
    setQuery(e.target.value);

    // Auto-resize textarea
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  };

  const resetFilters = () => {
    setPersonaFilters({
      generation_target: '',
      actual_audience: '',
      location: '',
      genre: '',
      profile_name: ''
    });
  };

  const resetChat = () => {
    setSelectedPersona(null);
    setChat([]);
    setSessionId(null); // Reset session ID when going back to selection
  };

  return (
    <div className="persona-interview-container">
      {!selectedPersona ? (
        // Persona Selection View
        <div className="persona-selection">
          <div className="page-header">
            <div className="header-content">
              <div className="header-title">
                <Users size={24} />
                <h1>Persona Interview Studio</h1>
              </div>
              <p>Select a persona to begin an interview session</p>
            </div>
          </div>

          {/* Filters */}
          <div className="filters-section">
            <div className="filters-header">
              <Filter size={20} />
              <h3>Filter Personas</h3>
              <button onClick={resetFilters} className="reset-filters-btn">
                Reset Filters
              </button>
            </div>

            <div className="filters-grid">
              <div className="filter-group">
                <label>Seed Artist/Genre</label>
                <input
                  type="text"
                  value={personaFilters.generation_target}
                  onChange={(e) => setPersonaFilters(prev => ({ ...prev, generation_target: e.target.value }))}
                  placeholder="e.g. Jelly Roll, Taylor Swift"
                />
              </div>
              <div className="filter-group">
                <label>Actual Audience</label>
                <input
                  type="text"
                  value={personaFilters.actual_audience}
                  onChange={(e) => setPersonaFilters(prev => ({ ...prev, actual_audience: e.target.value }))}
                  placeholder="e.g. working class, blue collar, country music fans"
                />
              </div>

              <div className="filter-group">
                <label>Location</label>
                <input
                  type="text"
                  value={personaFilters.location}
                  onChange={(e) => setPersonaFilters(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g. Kentucky, Nashville"
                />
              </div>

              <div className="filter-group">
                <label>Genre</label>
                <input
                  type="text"
                  value={personaFilters.genre}
                  onChange={(e) => setPersonaFilters(prev => ({ ...prev, genre: e.target.value }))}
                  placeholder="e.g. country, rock, pop"
                />
              </div>

              <div className="filter-group">
                <label>Profile Name</label>
                <input
                  type="text"
                  value={personaFilters.profile_name}
                  onChange={(e) => setPersonaFilters(prev => ({ ...prev, profile_name: e.target.value }))}
                  placeholder="Search by name"
                />
              </div>
            </div>
          </div>

          {/* Loading State */}
          {personasLoading && (
            <div className="personas-loading">
              <div className="loading-spinner"></div>
              <p>Loading personas...</p>
            </div>
          )}

          {/* Error State */}
          {personasError && (
            <div className="personas-error">
              <p>{personasError}</p>
              <button
                onClick={() => window.location.reload()}
                className="retry-btn"
              >
                Retry
              </button>
            </div>
          )}

          {/* Persona Grid */}
          {!personasLoading && !personasError && (
            <div className="personas-grid">
              {filteredPersonas.map(persona => (
              <div key={persona.id} className="persona-card">
                <div className="persona-header">
                  <div className="persona-avatar">
                    <User size={32} />
                  </div>
                  <div className="persona-info">
                    <h3
                      className="persona-name-link"
                      onClick={() => navigate(`/persona-profile/${persona.id}`)}
                    >
                      {persona.name}
                      <ExternalLink size={14} />
                    </h3>
                    <p className="persona-title">{persona.title}</p>
                    <p className="persona-company">{persona.company}</p>
                  </div>
                </div>

                <div className="persona-details">
                  <p className="persona-description">{persona.description}</p>

                  <div className="persona-meta">
                    <span className="meta-badge industry">{persona.industry}</span>
                    <span className="meta-badge department">{persona.department}</span>
                    <span className="meta-badge experience">{persona.experience}</span>
                  </div>

                  <div className="persona-traits">
                    {persona.traits.map(trait => (
                      <span key={trait} className="trait-tag">{trait}</span>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => startConversation(persona)}
                  className="start-interview-btn"
                >
                  <MessageSquare size={16} />
                  Start Interview
                </button>
              </div>
            ))}
            </div>
          )}

          {filteredPersonas.length === 0 && !personasLoading && !personasError && (
            <div className="no-personas">
              <Users size={48} />
              <h3>No personas match your filters</h3>
              <p>Try adjusting your filter criteria to see more personas</p>
              <button onClick={resetFilters} className="reset-filters-btn">
                Reset Filters
              </button>
            </div>
          )}
        </div>
      ) : (
        // Chat Interface View
        <div className="chat-interface">
          {/* Chat Header */}
          <div className="chat-header">
            <div className="chat-header-content">
              <div className="persona-info">
                <div className="persona-avatar">
                  <User size={24} />
                </div>
                <div className="persona-details">
                  <h2>{selectedPersona.name}</h2>
                  <p>{selectedPersona.title} at {selectedPersona.company}</p>
                </div>
              </div>

              <button onClick={resetChat} className="back-btn">
                ← Back to Selection
              </button>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="chat-messages" ref={chatWindowRef}>
            {chat.map((message) => (
              <div key={message.id} className={`message-group ${message.type}`}>
                <div className={`message-avatar ${message.type}`}>
                  {message.type === 'persona' ? <User size={20} /> : <Bot size={20} />}
                </div>
                <div className="message-content">
                  <div className={`message-bubble ${message.type}`}>
                    <div
                      className="message-text"
                      dangerouslySetInnerHTML={{ __html: message.content }}
                    />
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div className="persona-typing-wrapper">
                <div className="persona-thinking-avatar">
                  <User size={20} />
                </div>
                <div className="persona-thinking-bubble">
                  <div className="persona-thinking-text">
                    <Loader2 className="spinner" size={16} />
                    <span>{selectedPersona.name} is thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Chat Input */}
          <div className="chat-input-area">
            <div className="input-container">
              <textarea
                ref={textareaRef}
                value={query}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder={`Ask ${selectedPersona.name} about their role, experiences, or perspectives...`}
                className="message-input"
                rows={1}
                disabled={loading}
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!query.trim() || loading}
                className="send-btn"
              >
                <Send size={16} />
              </button>
            </div>
            <div className="input-hint">
              <p>Press Enter to send, Shift+Enter for new line</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PersonaInterview;